/* eslint-disable no-underscore-dangle */
/**
 * Everything you can DO to the crew of one trip, in the portal's own bottom
 * sheet — the second tier of the trip screen's Crew card.
 *
 * WHY IT EXISTS. The card used to carry five controls of identical weight:
 * Message, Call, Open their screen, Change, Add someone. On a phone that is
 * a wrapped hedge of 14px chips, all equally easy to hit by accident, and
 * one of them hands you somebody else's identity. They are not one kind of
 * action, so they should not be one row of buttons:
 *
 *   contact the driver   frequent, urgent      — stays ON the card, biggest
 *   edit the crew        occasional            — in here
 *   act as the driver    rare, consequential   — in here, set apart
 *
 * So the card keeps Message and Call and gains ONE quiet entry point, and
 * the two occasional jobs plus the one dangerous one live behind it. Two
 * taps instead of one, on targets the width of the sheet instead of chips —
 * which for a one-handed dispatcher in a hurry is the better trade.
 *
 * A BOTTOM SHEET, NOT AN OVERFLOW MENU. The portal already speaks this
 * language everywhere (DayPickerSheet, NoShowSheet, OvertimeReportSheet,
 * ExpenseSheet, AssignSheet), and a second idiom in the same app would be
 * worse than the five buttons were. It is also the thumb end of the screen,
 * which a kebab in the top corner is not.
 *
 * ACTING AS THE DRIVER IS SET APART ON PURPOSE. `AssumedDriverBar` says its
 * way out "is a button, not a back gesture, because leaving is a thing you
 * should have to mean"; the way IN deserves the same care. It sits under its
 * own rule and its own heading, below the ordinary jobs, with the
 * consequence written next to it in the bar's own words — and it is never
 * the primary action on the card or in here. It is offered only to an
 * administrator with a driver to be: the API refuses everyone else, and this
 * does not dangle what would be refused.
 *
 * It DECIDES nothing itself. Each row closes the sheet and hands the job
 * back to the trip screen, which owns the assign sheet and the navigation.
 */
import React from 'react';
import BottomSheet from '../Components/BottomSheet';
import { formatTime } from '../../../Utils/timezone';
import { driverText, roleLabel, shortDay } from '../Operator/operatorFormat';
import { operatorName } from './dispatcherFormat';

/** A trip the office has closed takes no assignment — the API refuses one. */
export const takesAssignment = (trip) => trip?.status === 'Confirmed';

/**
 * Whether the card has a second tier at all.
 *
 * A closed trip with no administrator looking at it has nothing in here, and
 * then the card says "Closed" where the control would have been rather than
 * opening an empty sheet.
 */
export const hasCrewActions = (trip, { mayAssume = false } = {}) => (
  takesAssignment(trip) || mayAssume
);

export default function CrewSheet({
  trip, mayAssume = false, onChange, onAdd, onViewAsDriver, onClose,
}) {
  const open = takesAssignment(trip);
  // Adding a second person only means something once somebody is on it: a
  // trip with nobody has Assign, which is the same sheet with the first slot
  // in it.
  const canAdd = open && Boolean(trip?.operator);
  const driver = driverText(trip);

  const subtitle = [
    trip?.booking_reference,
    `${shortDay(trip?.date_datetime)} ${formatTime(trip?.date_datetime)}`.trim(),
    trip?.operator ? operatorName(trip) : 'No operator',
  ].filter(Boolean).join(' · ');

  return (
    <BottomSheet title="Crew" subtitle={subtitle} onClose={onClose} closeLabel="Close">
      <div className="pp-dispatch__crew-sheet">
        {/* Who is on it now, so the sheet is not a menu floating free of the
            trip it is about. */}
        <p className="pp-op-form__hint">
          {[
            driver || 'No driver assigned',
            trip?.role && trip.role !== 'driver' ? roleLabel(trip.role) : '',
          ].filter(Boolean).join(' · ')}
        </p>

        {open ? (
          <button
            type="button"
            className="pp-btn pp-btn--primary pp-btn--block"
            onClick={onChange}
            // The accessible name CONTAINS the visible label (WCAG 2.5.3):
            // a voice-control user says what they can read.
            aria-label={trip?.operator
              ? `Change operator, driver & vehicle on ${trip?.booking_reference}`
              : `Assign an operator to ${trip?.booking_reference}`}
          >
            {trip?.operator ? 'Change operator, driver & vehicle' : 'Assign an operator'}
          </button>
        ) : (
          <p className="pp-op-form__hint">
            This trip is closed, so the crew on it can no longer be changed.
          </p>
        )}

        {canAdd ? (
          <button
            type="button"
            className="pp-btn pp-btn--outline pp-btn--block"
            onClick={onAdd}
            aria-label={`Add someone to ${trip?.booking_reference}`}
          >
            Add someone
          </button>
        ) : null}

        {/* Set apart, named, and last: this is the one action in here that
            makes you somebody else. */}
        {mayAssume ? (
          <div className="pp-dispatch__crew-sheet__danger">
            <h3 className="pp-dispatch__crew-sheet__heading">Act as this driver</h3>
            <p className="pp-op-form__hint">
              {`You will be working in ${driver || 'this driver'}'s own screen, and anything `
                + 'you do there is recorded as theirs.'}
            </p>
            <button
              type="button"
              className="pp-btn pp-btn--quiet pp-btn--block"
              onClick={onViewAsDriver}
              aria-label={`Open ${driver || 'the driver'}'s own screen`}
            >
              Open their screen
            </button>
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
}
